import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Lightbulb } from 'lucide-react';

/**
 * ScanErrorAlert - Component for displaying scan errors with actionable tips
 * 
 * Provides context-aware suggestions based on error type:
 * - No barcode detected → Adjust lighting/angle
 * - Product not found → Use manual entry
 * - Network errors → Retry suggestion
 */
const ScanErrorAlert = ({ 
  error, 
  onDismiss,
  t = (key, fallback) => fallback 
}) => {
  if (!error) return null;
  
  // Determine the appropriate tip based on error message
  const getTip = (errorMessage) => {
    const msg = errorMessage.toLowerCase();
    
    if (msg.includes('no barcode') || msg.includes('not detected') || msg.includes('scan failed')) {
      return {
        icon: '📸',
        text: 'Try adjusting the lighting or camera angle. Make sure the barcode is flat and well-lit.'
      };
    }
    
    if (msg.includes('not found') || msg.includes('unknown product') || msg.includes('no results')) {
      return {
        icon: '✏️',
        text: 'This product isn\'t in our database. Use "Manual Entry" mode to add it with custom details.'
      };
    }
    
    if (msg.includes('network') || msg.includes('timeout') || msg.includes('connection')) {
      return {
        icon: '🔄',
        text: 'Network issue detected. Check your internet connection and try again.'
      };
    }
    
    if (msg.includes('minimum') || msg.includes('at least 2')) {
      return {
        icon: '📦',
        text: 'DealShaq requires at least 2 items per batch to ensure value for shoppers.'
      };
    }
    
    if (msg.includes('camera') || msg.includes('permission')) {
      return {
        icon: '🎥',
        text: 'Camera access is blocked. Please enable camera permissions in your browser settings.'
      };
    }
    
    return null;
  };
  
  const tip = getTip(error);
  
  return (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        <div className="space-y-2">
          <p className="font-medium">{error}</p>
          
          {tip && (
            <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 rounded text-sm">
              <span>{tip.icon}</span>
              <p className="text-red-800">{tip.text}</p>
            </div>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default ScanErrorAlert;
